/**
 * debugReportHangProof — size caps + downscale logic for debug report
 * submit payloads.
 *
 * Extracted from debugReportCapture.ts 2026-04-25 for guard-file-size
 * compliance AND because the hang-proofing contract deserves its own
 * testable surface. Constants here ARE the specification — if debug
 * reports start hanging again, the answer is one of these tunables.
 *
 * Anchor incident: Steffen 2026-04-25 19:40. Debug-report submit hung
 * >1h on mobile, phone battery died before the multi-MB payload
 * completed upload. Root cause: no client-side timeout, no screenshot
 * size cap, no console buffer cap at export time.
 *
 * FR-DEBUG-REPORT-SUBMIT-HANGS-LARGE-PAYLOAD (atc ticket).
 */

import * as htmlToImage from 'html-to-image'
import type { ConsoleEntry } from './debugReportCapture'

// ── Size + time caps (tunable, test-asserted ranges) ─────────────────────

/** Abort the submit after this long; caller's fallback fires. */
export const REPORT_SUBMIT_TIMEOUT_MS = 30_000

/**
 * Per-tier timeout for the screenshot RENDER (`html-to-image.toJpeg`).
 *
 * The byte-cap below only applies AFTER toJpeg resolves. On a large/complex
 * DOM (the "large payload" failure mode) toJpeg can hang indefinitely while
 * serialising `document.body` to an image, so the cap never runs and the whole
 * collect/submit flow hangs (operator P0 BUG-2026-05-31-XT6GH). Racing each
 * tier against this timeout guarantees a hung render aborts so we fall through
 * to the next (smaller) tier or omit the screenshot — the report still goes
 * through. Three tiers × this budget stays well under REPORT_SUBMIT_TIMEOUT_MS.
 */
export const SCREENSHOT_RENDER_TIMEOUT_MS = 8_000

/** Hard cap on the base64-encoded screenshot portion of the payload. */
export const MAX_SCREENSHOT_BYTES = 500_000

/** Console entries kept in the EXPORTED payload (in-memory buffer untouched). */
export const EXPORT_MAX_CONSOLE_ENTRIES = 250

/** Per-entry character limit at export time. In-memory buffer caps per
 *  entry at 2000 (see pushConsole). This trims further for submit payloads. */
export const EXPORT_MAX_CONSOLE_CHARS = 500

/** Screenshot quality/pixelRatio tuples tried in order, widest→tightest. */
const SCREENSHOT_DOWNSCALE_TIERS: Array<{ quality: number; maxPixelRatio: number }> = [
  { quality: 0.7, maxPixelRatio: 1 },
  { quality: 0.5, maxPixelRatio: 0.75 },
  { quality: 0.3, maxPixelRatio: 0.5 },
]

// ── Pure helpers (unit-testable) ─────────────────────────────────────────

/**
 * Estimate the byte size of a data URL. For a base64 data URL, the actual
 * payload bytes are ~75% of the data-URL string length (3 bytes per 4 chars).
 * Pure function, unit-testable without a canvas.
 */
export function estimateDataUrlBytes(dataUrl: string): number {
  const commaIdx = dataUrl.indexOf(',')
  if (commaIdx < 0) return dataUrl.length
  const base64 = dataUrl.slice(commaIdx + 1)
  return Math.floor((base64.length * 3) / 4)
}

/**
 * Race a promise against a timeout. The loser is NOT cancelled (html-to-image
 * has no abort hook), but rejecting frees the caller so a hung render can never
 * block the debug-report flow. Pure + unit-testable with fake timers.
 */
export function withTimeout<T>(work: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), timeoutMs)
  })
  return Promise.race([work, timeout]).finally(() => {
    if (timer) clearTimeout(timer)
  })
}

/**
 * Trim the console buffer for SUBMIT payloads. Keeps the most recent
 * EXPORT_MAX_CONSOLE_ENTRIES entries and truncates each message to
 * EXPORT_MAX_CONSOLE_CHARS. Most recent wins — earliest entries drop first
 * because the tail of the buffer is the most relevant context for the
 * user's current bug.
 *
 * The in-memory buffer (getConsoleBuffer / filterConsoleNoise) is
 * untouched — full 500 × 2000 locally for downloadBundle users.
 */
export function trimConsoleForExport(entries: ConsoleEntry[]): ConsoleEntry[] {
  const tail = entries.slice(-EXPORT_MAX_CONSOLE_ENTRIES)
  return tail.map(e => ({
    ...e,
    message: e.message.length > EXPORT_MAX_CONSOLE_CHARS
      ? e.message.slice(0, EXPORT_MAX_CONSOLE_CHARS) + `… [truncated, was ${e.message.length} chars]`
      : e.message,
  }))
}

// ── Screenshot with iterative downscale ──────────────────────────────────

/**
 * Iterate quality/pixelRatio downward until the JPEG fits under
 * MAX_SCREENSHOT_BYTES or we exhaust the tiers. Returns the FIRST
 * fitting capture — typically quality=0.7 on normal-sized pages; falls
 * back to 0.5, 0.3 for pathological long pages. When no tier fits,
 * returns null + error marker so the submit still goes through without
 * the screenshot (screenshotError records the reason for Support).
 */
export async function captureScreenshot(): Promise<{ dataUrl: string | null; error: string | null }> {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280
  const tryErrors: string[] = []

  for (const tier of SCREENSHOT_DOWNSCALE_TIERS) {
    const pixelRatio = Math.min(tier.maxPixelRatio, 1280 / viewportWidth)
    try {
      // Race the render against a per-tier timeout: a hung toJpeg on a large
      // DOM must not block the flow (the byte-cap below only runs once it
      // resolves). On timeout we throw and fall through to the next tier.
      const dataUrl = await withTimeout(
        htmlToImage.toJpeg(document.body, {
          quality: tier.quality,
          pixelRatio,
          filter: (node) => {
            if (!(node instanceof HTMLElement)) return true
            if (node.tagName === 'IFRAME') return false
            if (node.getAttribute('role') === 'dialog') return false
            return true
          },
          cacheBust: true,
        }),
        SCREENSHOT_RENDER_TIMEOUT_MS,
        `screenshot render (q=${tier.quality})`,
      )
      const bytes = estimateDataUrlBytes(dataUrl)
      if (bytes <= MAX_SCREENSHOT_BYTES) return { dataUrl, error: null }
      tryErrors.push(`tier(q=${tier.quality}, pr=${pixelRatio.toFixed(2)}) → ${bytes} bytes > cap ${MAX_SCREENSHOT_BYTES}`)
    } catch (err) {
      tryErrors.push(`tier(q=${tier.quality}) threw: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  return {
    dataUrl: null,
    error: `screenshot omitted — all downscale tiers exceeded ${MAX_SCREENSHOT_BYTES} bytes: ${tryErrors.join('; ')}`,
  }
}
